import { z } from "zod";

const nullableText = (maximum: number) => z.string().max(maximum).nullable();
const nullableNonNegative = z.number().nonnegative().nullable();
const date = z.iso.date();

const protocolItemSchema = z
  .object({
    substancia: z.string().trim().min(1).max(300),
    dose: nullableText(300).optional(),
    dose_valor: z.number().positive().nullable().optional(),
    dose_unidade: nullableText(30).optional(),
    via: nullableText(200).optional(),
    frequencia: nullableText(500).optional(),
    // Opcional por paridade com o schema aplicado e com `aprovar_ai_draft`,
    // que faz `coalesce(frequencia_tipo, 'texto')`.
    frequencia_tipo: z.enum(["texto", "intervalo_dias", "dias_semana", "sob_demanda"]).optional(),
    frequencia_intervalo_dias: z.number().int().min(1).max(365).nullable().optional(),
    frequencia_dias_semana: z
      .array(z.number().int().min(0).max(6))
      .max(7)
      .refine((days) => new Set(days).size === days.length, "dias repetidos")
      .nullable()
      .optional(),
    primeira_aplicacao_em: z.iso.datetime().nullable().optional(),
    horario: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
      .nullable()
      .optional(),
    fase: nullableText(300).optional(),
    observacoes: nullableText(2000).optional(),
  })
  .strict()
  .superRefine((item, context) => {
    if ((item.dose_valor == null) !== (item.dose_unidade == null)) {
      context.addIssue({
        code: "custom",
        message: "dose_valor e dose_unidade devem aparecer juntos",
      });
    }
    if (item.frequencia_tipo === "intervalo_dias" && item.frequencia_intervalo_dias == null) {
      context.addIssue({ code: "custom", message: "intervalo requer a quantidade de dias" });
    }
    if (item.frequencia_tipo === "dias_semana" && !item.frequencia_dias_semana?.length) {
      context.addIssue({ code: "custom", message: "informe os dias da semana" });
    }
  });

/** `data_fim_prevista` nunca pode anteceder `data_inicio`. */
function checkPeriod(
  value: { data_inicio?: string | null | undefined; data_fim_prevista?: string | null | undefined },
  context: z.RefinementCtx,
): void {
  if (value.data_inicio && value.data_fim_prevista && value.data_fim_prevista < value.data_inicio) {
    context.addIssue({
      code: "custom",
      message: "data_fim_prevista anterior a data inicial",
      path: ["data_fim_prevista"],
    });
  }
}

export const protocolArtifactSchema = z
  .object({
    titulo: z.string().trim().min(1).max(300),
    objetivo: nullableText(2000).optional(),
    data_inicio: date.nullable().optional(),
    data_fim_prevista: date.nullable().optional(),
    itens: z.array(protocolItemSchema).min(1).max(100),
  })
  .strict()
  .superRefine(checkPeriod);
export type ProtocolArtifactPayload = z.infer<typeof protocolArtifactSchema>;

const trainingExerciseSchema = z
  .object({
    exercicio_nome: z.string().trim().min(1).max(200),
    series: z.number().int().min(1).max(100).nullable().optional(),
    repeticoes: nullableText(80).optional(),
    carga: nullableText(80).optional(),
    descanso_segundos: z.number().int().min(0).max(3600).nullable().optional(),
    instrucoes: nullableText(1000).optional(),
  })
  .strict();

const trainingDaySchema = z
  .object({
    nome: z.string().trim().min(1).max(160),
    observacoes: nullableText(1000).optional(),
    exercicios: z.array(trainingExerciseSchema).min(1).max(100),
  })
  .strict();

const trainingPhaseSchema = z
  .object({
    nome: z.string().trim().min(1).max(160),
    objetivo: nullableText(1000).optional(),
    duracao_semanas: z.number().int().min(1).max(104).nullable().optional(),
    dias: z.array(trainingDaySchema).min(1).max(31),
  })
  .strict();

export const trainingArtifactSchema = z
  .object({
    nome: z.string().trim().min(1).max(200),
    objetivo: nullableText(3000).optional(),
    restricoes: nullableText(3000).optional(),
    data_inicio: date.nullable().optional(),
    data_fim_prevista: date.nullable().optional(),
    fases: z.array(trainingPhaseSchema).min(1).max(24),
  })
  .strict()
  .superRefine(checkPeriod);
export type TrainingArtifactPayload = z.infer<typeof trainingArtifactSchema>;

const nutritionReplacementSchema = z
  .object({
    alimento: z.string().trim().min(1).max(240),
    quantidade: z.number().positive(),
    unidade: z.string().trim().min(1).max(60),
    calorias_kcal: nullableNonNegative.optional(),
    proteina_g: nullableNonNegative.optional(),
    carboidrato_g: nullableNonNegative.optional(),
    gordura_g: nullableNonNegative.optional(),
    valores_origem: z.enum([
      "nao_informado",
      "digitado_nao_validado",
      "gerado_nao_validado",
      "fonte_validada",
    ]),
    valores_fonte: nullableText(1000).optional(),
    observacoes: nullableText(1000).optional(),
  })
  .strict();

const nutritionItemSchema = nutritionReplacementSchema.extend({
  substituicoes: z.array(nutritionReplacementSchema).max(30).optional(),
});

const nutritionMealSchema = z
  .object({
    nome: z.string().trim().min(1).max(160),
    horario: z
      .string()
      .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
      .nullable()
      .optional(),
    observacoes: nullableText(1000).optional(),
    itens: z.array(nutritionItemSchema).min(1).max(100),
  })
  .strict();

export const nutritionArtifactSchema = z
  .object({
    nome: z.string().trim().min(1).max(200),
    objetivo: nullableText(3000).optional(),
    meta_energia_kcal: nullableNonNegative.optional(),
    meta_proteina_g: nullableNonNegative.optional(),
    meta_carboidrato_g: nullableNonNegative.optional(),
    meta_gordura_g: nullableNonNegative.optional(),
    meta_fibra_g: nullableNonNegative.optional(),
    metas_origem: z.enum([
      "nao_informado",
      "digitado_nao_validado",
      "gerado_nao_validado",
      "fonte_validada",
    ]),
    metas_fonte: nullableText(1000).optional(),
    data_inicio: date.nullable().optional(),
    data_fim_prevista: date.nullable().optional(),
    refeicoes: z.array(nutritionMealSchema).min(1).max(30),
  })
  .strict()
  .superRefine(checkPeriod);
export type NutritionArtifactPayload = z.infer<typeof nutritionArtifactSchema>;

const examValueSchema = z
  .object({
    nome: z.string().trim().min(1).max(300),
    valor: z.string().trim().min(1).max(300),
    unidade: nullableText(100).optional(),
    referencia: nullableText(500).optional(),
    classificacao: z.enum(["baixo", "normal", "alto", "indeterminado"]).optional(),
  })
  .strict();

export const examArtifactSchema = z
  .object({
    tipo_exame: nullableText(300).optional(),
    data: date.nullable().optional(),
    protocolo_id: z.uuid().nullable().optional(),
    valores: z.array(examValueSchema).max(500),
    resumo: nullableText(4000).optional(),
    arquivo_asset_id: nullableText(500).optional(),
  })
  .strict();
export type ExamArtifactPayload = z.infer<typeof examArtifactSchema>;

export const bodyAssessmentArtifactSchema = z
  .object({
    data: date,
    peso_kg: z.number().positive().max(1000),
    altura_cm: z.number().min(30).max(300).nullable().optional(),
    percentual_gordura: z.number().min(0).max(100).nullable().optional(),
    massa_magra_kg: z.number().min(0).max(1000).nullable().optional(),
    medidas: z.record(z.string().trim().min(1).max(80), z.number().nonnegative()).optional(),
    composicao: z.record(z.string().trim().min(1).max(80), z.number().nonnegative()).optional(),
    protocolo_avaliacao: nullableText(300).optional(),
    observacoes: nullableText(4000).optional(),
  })
  .strict()
  .superRefine((assessment, context) => {
    for (const [key, value] of Object.entries(assessment.medidas ?? {})) {
      if (key.startsWith("dobra_") && (value <= 0 || value > 200)) {
        context.addIssue({
          code: "custom",
          path: ["medidas", key],
          message: "dobra cutânea deve estar entre 0 e 200 mm",
        });
      }
    }
  });
export type BodyAssessmentArtifactPayload = z.infer<typeof bodyAssessmentArtifactSchema>;

export const artifactTypeSchema = z.enum([
  "protocolo",
  "treino",
  "nutricao",
  "exame",
  "avaliacao_corporal",
]);
export type ArtifactType = z.infer<typeof artifactTypeSchema>;

export const artifactSchemas = {
  protocolo: protocolArtifactSchema,
  treino: trainingArtifactSchema,
  nutricao: nutritionArtifactSchema,
  exame: examArtifactSchema,
  avaliacao_corporal: bodyAssessmentArtifactSchema,
} as const satisfies Record<ArtifactType, z.ZodType>;

export function parseArtifactPayload(type: ArtifactType, payload: unknown): unknown {
  return artifactSchemas[type].parse(payload);
}

/** Problemas do payload segundo o schema canônico, sem repetir nenhum valor. */
export function artifactPayloadIssues(
  type: ArtifactType,
  payload: unknown,
): readonly z.core.$ZodIssue[] {
  const result = artifactSchemas[type].safeParse(payload);
  return result.success ? [] : result.error.issues;
}
